import {Component} from 'panel';
// need the pollyfill since IE + firefox on android aren't supported yet
import ResizeObserver from 'resize-observer-polyfill';
import isNumber from 'lodash/isNumber';

type Constructor<T = UnknownObj> = new (...args: any[]) => T;

type DefaultKey = `default`;
type SizeKeys = number | DefaultKey;
type SizeTuple<EnumT> = [SizeKeys, EnumT];

export type ComponentSizes<T> = Array<SizeTuple<T>>;

export interface ResizableState<T> {
  width: T;
  height: T;
}

export interface ResizableStatePartial<T> {
  resizable: ResizableState<T>;
}

export const NullSizeMap: ComponentSizes<null> = [[`default`, null]];

export const getDefaultSize = <EnumT>(arr: ComponentSizes<EnumT>): EnumT =>
  arr.find((element) => element[0] === `default`)[1];

function getSizeValue<EnumT>(size: number, sizes: ComponentSizes<EnumT>): EnumT {
  // Filter out any non-numerical sizes
  const numberSizes = sizes.filter((size) => isNumber(size[0])) as Array<[number, EnumT]>;

  numberSizes.sort((a, b) => a[0] - b[0]);

  for (let i = numberSizes.length - 1; i >= 0; i--) {
    if (size >= numberSizes[i][0]) {
      return numberSizes[i][1];
    }
  }

  // This can happen if they don't specify any non-default sizes (i.e. they're only tracking one of width and height)
  return getDefaultSize(sizes);
}

/**
 * Mixin that observes an element and updates state when width or height changes
 * @param widthSizes - map of component sizes -> width states
 * @param heightSizes - map of component sizes -> height states
 * @param elementSelector - optional selector to observe instead of this.el
 */
export const ResizableMixin = <
  EnumT,
  StateT,
  AttrsT,
  AppStateT,
  TBase extends Constructor<Component<StateT & ResizableStatePartial<EnumT>, AttrsT, AppStateT>>
>(
  widthSizes: ComponentSizes<EnumT>,
  heightSizes: ComponentSizes<EnumT>,
  elementSelector?: string,
) => (superclass: TBase): TBase =>
  class extends superclass {
    private resizeObserver: ResizeObserver;

    constructor(..._args) {
      super();

      // Set initial resize state
      this.update({
        ...this.state,
        resizable: {
          width: getDefaultSize(widthSizes),
          height: getDefaultSize(heightSizes),
        },
      });
    }

    connectedCallback() {
      super.connectedCallback();

      this.resizeObserver = new ResizeObserver((entries) => {
        // Since our ResizeObserver only observes one element, grab the first entry
        const {width, height} = entries[0].contentRect;
        this._updateResizableState(width, height);
      });

      const element = elementSelector ? this.el.querySelector(elementSelector) : this.el;

      // In some race conditions, even if the component is initalized, the element  can have an incomplete
      // body (i.e. the body  is just <div></div> even though the template doesn't look like that
      // TODO(sam) do some investigation to determine why this happens/how to prevent (likely a snabdom issue or race condition)
      if (element) {
        this.resizeObserver.observe(element);

        const {width, height} = element.getBoundingClientRect();
        this._updateResizableState(width, height);
      }
    }

    disconnectedCallback() {
      this.resizeObserver.disconnect();

      super.disconnectedCallback();
    }

    _updateResizableState(width, height) {
      const widthUpdate = getSizeValue(width, widthSizes);
      const heightUpdate = getSizeValue(height, heightSizes);

      this.update({
        ...this.state,
        resizable: {
          width: widthUpdate,
          height: heightUpdate,
        },
      });
    }
  };
